/**
 * DashboardContractBuilder Component
 * 
 * Authenticated version of contract builder for employers in their dashboard.
 * Automatically links to employer account and existing employees.
 */

import { useState, useEffect } from 'react';
import { ContractEditor } from './ContractEditor';
import { AIChatSidebar } from './AIChatSidebar';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ApiService from '@/services/api';
import { EndpointKeys } from '@/config/api';
import { useAuth } from '@/hooks/useAuth';
import { Download, FileText, Save } from 'lucide-react';

interface DashboardContractBuilderProps {
  employeeId?: number;
  onComplete?: (contractId: string) => void;
}

export const DashboardContractBuilder: React.FC<DashboardContractBuilderProps> = ({
  employeeId,
  onComplete,
}) => {
  const { user } = useAuth();
  const [contractId, setContractId] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | undefined>(employeeId);

  useEffect(() => {
    initializeContract();
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const response = await ApiService.request<{
        employees: any[];
      }>(EndpointKeys.GET_EMPLOYEES, {
        method: 'GET',
      });
      setEmployees(response.employees || []);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  const initializeContract = async () => {
    if (!user?.email) return;

    try {
      const sessionId = `employer_${user.id}_${Date.now()}`;

      const response = await ApiService.request<{
        contract_id: string;
        html_content: string;
      }>(EndpointKeys.CONTRACT_BUILDER_CREATE, {
        method: 'POST',
        body: {
          user_email: user.email,
          user_first_name: (user as any).first_name || '',
          user_last_name: (user as any).last_name || '',
          session_id: sessionId,
          employee_id: employeeId,
        },
      });

      setContractId(response.contract_id);
      setHtmlContent(response.html_content);
    } catch (error) {
      console.error('Failed to create contract:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAIEdit = (updatedHtml: string, changes: string[]) => {
    setHtmlContent(updatedHtml);
  };

  const handleContentChange = (html: string) => {
    setHtmlContent(html);
  };

  const handleSave = async () => {
    try {
      await ApiService.request(EndpointKeys.CONTRACT_BUILDER_SAVE, {
        method: 'POST',
        urlParams: { contractId },
        body: {
          html_content: htmlContent,
          structured_data: {},
          change_description: 'Manual save from dashboard',
        },
      });
      alert('Contract saved successfully!');
    } catch (error) {
      console.error('Failed to save contract:', error);
      alert('Failed to save contract. Please try again.');
    }
  };

  const handleDownload = async () => {
    // Placeholder for PDF generation
    alert('PDF generation coming soon!');
  };

  const handleComplete = async () => {
    if (onComplete && contractId) {
      onComplete(contractId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading contract builder...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Employment Contract Builder</h2>
              <p className="text-sm text-gray-500 mt-1">
                Create a contract with AI assistance
              </p>
            </div>
            {!employeeId && employees.length > 0 && (
              <Select 
                value={selectedEmployeeId?.toString()}
                onValueChange={(value) => setSelectedEmployeeId(parseInt(value))}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id.toString()}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} variant="outline">
              <Save className="mr-2 h-4 w-4" />
              Save
            </Button>
            <Button onClick={handleDownload} variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
            {onComplete && (
              <Button onClick={handleComplete}>
                <FileText className="mr-2 h-4 w-4" />
                Complete
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main interface */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor (left 60%) */}
        <div className="flex-[6] overflow-auto bg-white border-r border-gray-200">
          <ContractEditor
            initialHtml={htmlContent}
            onContentChange={handleContentChange}
            isLocked={isAIProcessing}
          />
        </div>

        {/* AI Chat (right 40%) */}
        <div className="flex-[4] overflow-hidden">
          <AIChatSidebar
            contractId={contractId}
            currentHtml={htmlContent}
            onAIEdit={handleAIEdit}
            isProcessing={isAIProcessing}
            setIsProcessing={setIsAIProcessing}
          />
        </div>
      </div>
    </div>
  );
};

